import { View, Text, StyleSheet, TouchableOpacity } from "react-native";
import React, { useEffect, useState } from "react";

import GlobalStyles from "../../Resources/Styles";
import Colors from "../../Resources/Color";
import Fonts from "../../Resources/Fonts";
import { FontAwesome5, Ionicons } from "@expo/vector-icons";
import { Feather } from "@expo/vector-icons";

import { observer, Observer } from "mobx-react";
import { useTranslation } from "react-i18next";

import { useScreenDimensions, useDevice } from "../_layout";
import IconEmptyView from "../Components/IconEmptyView";

import PredefinedAttributeLayoutView from "../Exercise/PredefinedAttributeLayoutView";
import SearchBar from "../Components/SearchBar";
import PredefinedExerciseLayoutView from "../Exercise/PredefinedExerciseLayoutView";
import { formatNumber, formatNumber2Dec } from "../utils/numberUtils";

const InfoView = observer(
  ({
    client,
    block = null,
    exercise,
    coach = null,
    coachLibrary = false,
    workoutView = false,
    calendar = false,
  }) => {
    const { t, i18n } = useTranslation();
    const { screenSize } = useScreenDimensions();
    const { isMobile } = useDevice();

    // const blockDashboard = !!exercise?.block;
    // const block = exercise?.block;
    const coachAux = coach ? coach : client.coach;

    const TabBar = observer(() => {
      const TabButton = observer(({ isModifiers }) => {
        const [isHovered, setIsHovered] = useState(false);

        const handlePress = () => {
          if (block) {
            block.setProperty("isProtocolAttributesShowing", isModifiers);
          } else {
            client.setProperty("isHistoryShowing", !isModifiers);
          }
        };

        const isActive = block
          ? isModifiers === block.isProtocolAttributesShowing
          : client.isHistoryShowing !== isModifiers;

        return (
          <TouchableOpacity
            onPress={handlePress}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <View style={{ alignItems: "center" }}>
              <Text
                style={[
                  styles.tabText,
                  {
                    color:
                      isActive || isHovered
                        ? Colors.bubbleColor
                        : Colors.blackColor,
                  },
                ]}
              >
                {isModifiers
                  ? t("modifiers-string")
                  : block
                  ? t("exercises-string")
                  : t("history-string")}
              </Text>
              <View
                style={[
                  styles.underline,
                  {
                    backgroundColor: isActive
                      ? Colors.bubbleColor
                      : "transparent",
                  },
                ]}
              />
            </View>
          </TouchableOpacity>
        );
      });

      return (
        <View
          style={[
            GlobalStyles.HStack,
            {
              marginVertical: 20,
              justifyContent: "space-evenly",
              flex: 1,
              flexDirection: "row",
            },
          ]}
        >
          <TabButton isModifiers={false} />
          <TabButton isModifiers={true} />
        </View>
      );
    });

    const HistoryView = () => {
      const HistoryRow = ({ exercise, isLast, isFirst }) => {
        const rirOrRpe = exercise?.workout?.block?.client?.coach?.rirOrRpe;

        function formatDate(date) {
          const d = new Date(date);
          let day = d.getDate();
          let month = d.getMonth() + 1; // Months are zero-based
          let year = d.getFullYear();

          // Pad day and month with a leading zero if they are less than 10
          day = day < 10 ? "0" + day : day;
          month = month < 10 ? "0" + month : month;

          // Convert year to two digits
          year = year && year.toString() ? year.toString().substr(-2) : year;

          return `${day}/${month}/${year}`;
        }

        const getBackgroundColor = (serie) => {
          if (!serie) return "rgba(0, 0, 0, 1)";

          const target = serie.getTargetRpeReference;
          const actual = serie.rpe;

          if (typeof target === "number" && target > 0.02 && actual > 0.02) {
            // round both to one decimal (to match your formatNumber logic)
            const a = parseFloat(formatNumber2Dec(actual));
            const t = parseFloat(formatNumber2Dec(target));

            if (Math.abs(a - t) >= 1) {
              return a > t ? "rgba(240, 68, 56, 1)" : "rgba(93, 156, 96, 1)";
            }
          }

          return "rgba(0, 0, 0, 1)";
        };
        return (
          <View
            style={{
              backgroundColor: Colors.whiteColor,
              backfaceVisibility: "hidden",
              borderBottomWidth: isLast ? 0 : 1,
              borderBottomColor: Colors.borderColor,
              alignItems: "center",
              justifyContent: "space-between",
              paddingTop: isFirst ? 0 : 16,
              paddingBottom: 16,
              paddingHorizontal: 8,
              flexDirection: "row",
              gap: 8,
            }}
          >
            <View
              style={{
                gap: 6,
                flexShrink: 1,
              }}
            >
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  flexWrap: "wrap",
                  columnGap: 8,
                  rowGap: 6,
                }}
              >
                <Text
                  numberOfLines={1}
                  style={{
                    color: Colors.blackColor,
                    fontWeight: "500",
                    marginLeft: -1,
                  }}
                >
                  {t("weekWithNumber-string", {
                    week: exercise.workout.week,
                  })}
                </Text>
                <Text
                  style={[
                    Fonts.regular12,
                    {
                      color: Colors.grayColor,
                    },
                  ]}
                >
                  {exercise.workout.date
                    ? formatDate(exercise.workout.date)
                    : ""}
                </Text>
              </View>
              <View style={[GlobalStyles.HStack, {}]}>
                <Ionicons
                  name="barbell-outline"
                  size={11}
                  color={Colors.bubbleColor}
                  style={GlobalStyles.rowIcon}
                />
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  style={{
                    ...GlobalStyles.rowSubText,
                    fontSize: 12,
                  }}
                >
                  {exercise.workout.name}
                </Text>
              </View>
              <View style={[GlobalStyles.HStack, {}]}>
                <Ionicons
                  name="clipboard-outline"
                  size={12}
                  color={Colors.bubbleColor}
                  style={GlobalStyles.rowIcon}
                />
                <Text
                  numberOfLines={1}
                  style={{
                    ...GlobalStyles.rowSubText,
                    fontSize: 12,
                  }}
                >
                  {exercise.workout.block.name}
                </Text>
              </View>
            </View>

            <View style={{ alignItems: "flex-end" }}>
              {exercise.getSortedSeries.map((serie, index) => (
                <Text
                  numberOfLines={1}
                  style={{
                    color: Colors.blackColor,
                    fontSize: 12,
                    marginVertical: 2,
                  }}
                  key={index}
                >
                  {formatNumber(serie.weight)} x {formatNumber(serie.reps)}{" "}
                  <Text
                    numberOfLines={1}
                    style={{
                      color: getBackgroundColor(serie),
                      fontSize: 12,
                      marginVertical: 2,
                    }}
                    key={index}
                  >
                    {rirOrRpe ? "RIR" : "@"}
                    {formatNumber(serie.rpe)}
                  </Text>
                </Text>
              ))}
            </View>
          </View>
        );
      };
      const SeeMoreButton = () => {
        const [isHovered, setIsHovered] = useState(false);

        return (
          <TouchableOpacity
            onPress={() =>
              client.setProperty(
                "shownBlocksInHistory",
                client.shownBlocksInHistory + 1
              )
            }
            style={{
              alignSelf: "center",
              marginBottom: 10,
              marginTop: 10,
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Text
              style={{
                color: Colors.bubbleColor,
                fontWeight: isHovered ? "500" : "normal",
              }}
            >
              {t("seeMore-string")}
            </Text>
          </TouchableOpacity>
        );
      };

      return (
        <View style={{ marginHorizontal: 12, marginBottom: 12 }}>
          {client.getExercisesForHistory(exercise.workout, t, i18n.language)
            .length === 0 ? (
            <IconEmptyView
              iconLibrary={"Ionicons"}
              mainText={"emptyHistoryTitle-string"}
              subText={"emptyHistoryText-string"}
              iconName={"list"}
              marginTop={32}
              marginBottom={16}
            />
          ) : (
            <Observer>
              {() => (
                <>
                  {client
                    .getExercisesForHistory(exercise.workout, t, i18n.language)
                    .map((exercise, index, array) => (
                      <HistoryRow
                        key={exercise.id}
                        exercise={exercise}
                        isFirst={index === 0}
                        isLast={index === array.length - 1}
                      />
                    ))}

                  {client.shownBlocksInHistory <
                    client.getTotalBlocksWithExercise(t, i18n.language) && (
                    <SeeMoreButton />
                  )}
                </>
              )}
            </Observer>
          )}
        </View>
      );
    };

    const AttributesView = observer(() => {
      // const changeKey = exercise?.id ? `${exercise.id}-${exercise.attributes?.map(attr => attr.name).join('-') ?? ''}` : block?.selectedProtocolSlot;
      const changeKey = exercise?.id
        ? exercise.getName(t, i18n.language)
        : block?.selectedProtocolSlot;
      return (
        <View
          style={{
            marginHorizontal: 12,
            marginBottom: 12,
            gap: 12,
          }}
        >
          <SearchBar
            text={coachAux.searchTermAttr}
            onChangeText={(text) => coachAux.setSearchTermAttr(text)}
            dark={false}
            placeholder={"searchAttributes-string"}
            focusOnChange={true}
            changeKey={changeKey}
          />
          <PredefinedAttributeLayoutView
            coach={coachAux}
            exercise={exercise}
            block={block}
          />
        </View>
      );
    });

    const ExercisesView = observer(() => {
      // const changeKey = exercise?.id ? `${exercise.id}-${exercise.attributes?.map(attr => attr.name).join('-') ?? ''}` : block?.selectedProtocolSlot;
      const changeKey = exercise?.id
        ? exercise.getName(t, i18n.language)
        : block?.selectedProtocolSlot;
      return (
        <View
          style={{
            marginHorizontal: 12,
            marginBottom: 12,
            gap: 12,
          }}
        >
          <SearchBar
            text={coachAux.searchTermPred}
            onChangeText={(text) => coachAux.setSearchTermPred(text)}
            dark={false}
            placeholder={"searchExercise-string"}
            focusOnChange={true}
            changeKey={changeKey}
          />
          <PredefinedExerciseLayoutView
            coach={coachAux}
            workout={exercise?.workout}
            exercise={exercise}
            block={block}
            calendar={calendar}
          />
        </View>
      );
    });

    return (
      <View
        style={{
          alignItems: "center",
          zIndex: 2,
        }}
      >
        {(exercise || block) && (
          <View
            style={{
              backgroundColor: "white",
              // width: "100%",
              width: screenSize === "small" ? "100%" : block ? 320 : "100%",
              borderRadius: 14,
              // paddingTop: block ? 8 : 16,
              paddingTop: block || screenSize === "small" ? 8 : 16,
              alignItems: "center",
              borderWidth: 0.1,
              borderColor: Colors.borderColor,
            }}
          >
            <View style={{ width: "100%" }}>
              <View>
                <Observer>
                  {() => (
                    <>
                      {(block || screenSize === "small") && (
                        <View style={{ alignItems: "flex-end" }}>
                          <TouchableOpacity
                            onPress={() => {
                              if (coach && workoutView) {
                                coach.setProperty("showingInfoView", false);
                              } else if (block) {
                                block.setProperty("showingInfoView", false);
                              } else if (client) {
                                client.setProperty("showingInfoView", false);
                              }
                            }}
                          >
                            <Feather
                              name="x"
                              size={24}
                              color={Colors.grayColor}
                              style={{
                                paddingHorizontal: 4,
                                paddingTop: 4,
                                marginRight: 12,
                              }}
                            />
                          </TouchableOpacity>
                        </View>
                      )}

                      {exercise ? (
                        <Text
                          style={[
                            Fonts.bold16,
                            {
                              textAlign: "center",
                              marginHorizontal: 12,
                            },
                          ]}
                        >
                          {exercise.getName(t, i18n.language)}
                        </Text>
                      ) : (
                        block && (
                          <Text
                            style={[
                              Fonts.bold16,
                              {
                                textAlign: "center",
                                marginHorizontal: 12,
                              },
                            ]}
                          >
                            {t(block.selectedProtocolSlot)}
                          </Text>
                        )
                      )}
                      {block && (
                        <Text
                          style={[
                            Fonts.medium12,
                            {
                              textAlign: "center",
                              marginHorizontal: 12,
                              color: Colors.grayColor,
                              marginTop: 8,
                            },
                          ]}
                          numberOfLines={1}
                        >
                          {calendar && (
                            <Text style={{ flexShrink: 1 }} numberOfLines={1}>
                              {block.name}
                              {" · "}
                            </Text>
                          )}
                          {t("weekWithNumber-string", {
                            week: exercise
                              ? exercise.getWeek
                              : block.selectedProtocolWeek,
                          })}
                        </Text>
                      )}
                    </>
                  )}
                </Observer>

                {(!coachLibrary || block) && (
                  <View style={{ flex: 1, flexDirection: "row" }}>
                    <TabBar />
                  </View>
                )}

                <Observer>
                  {() =>
                    block ? (
                      <>
                        {block?.isProtocolAttributesShowing ? (
                          <AttributesView />
                        ) : (
                          <ExercisesView />
                        )}
                      </>
                    ) : coachLibrary ? (
                      <View style={{ marginTop: 16 }}>
                        <AttributesView />
                      </View>
                    ) : (
                      <>
                        {client?.isHistoryShowing ? (
                          <HistoryView />
                        ) : (
                          <AttributesView />
                        )}
                      </>
                    )
                  }
                </Observer>
              </View>
            </View>
          </View>
        )}
      </View>
    );
  }
);

const styles = StyleSheet.create({
  tabText: {
    fontSize: 13,
    fontWeight: "500",
  },
  underline: {
    height: 2,
    width: "100%",
    alignSelf: "center",
    marginTop: 2,
  },
});

export default InfoView;
